import React from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExchangeAlt } from "@fortawesome/free-solid-svg-icons";
import { formatCurrency as formatCost } from "../../utils/currencyFormatter";
import { calculatePackageTotals } from "./pricingUtils";

const DesktopCompareDetails = ({ estimateData }) => {
  const packages = estimateData?.packages;
  if (!packages || packages.length < 2) return null;

  const comparePkgs = packages.slice(0, 2);
  const currency = estimateData.currency || "INR";
  const includeTransport = (estimateData?.includeTransport ?? estimateData?.input?.includeTransport ?? estimateData?.transport?.includeTransport) !== false;

  const getPrice = (pkg, type) => {
    const { 
      transportAmount, grandTotalMid,
      perPersonDoubleSharingMid, adultCostMid, aebCostMid, cwbCostMid, cnbCostMid
    } = calculatePackageTotals(pkg, estimateData);
    
    switch(type) {
      case 'perPerson':
        return perPersonDoubleSharingMid ? formatCost(perPersonDoubleSharingMid, currency) : '-';
      case 'aeb':
        return aebCostMid ? formatCost(aebCostMid, currency) : 'N/A';
      case 'cwb':
        return cwbCostMid ? formatCost(cwbCostMid, currency) : 'N/A';
      case 'cnb':
        return cnbCostMid ? formatCost(cnbCostMid, currency) : 'N/A';
      case 'landTotal':
        return formatCost(pkg.breakdownTotal || 0, currency);
      case 'transport':
        return formatCost(transportAmount, currency);
      case 'grandTotal':
        return formatCost(grandTotalMid, currency);
      default:
        return '-';
    }
  };

  return (
    <div className="border border-[#e8ebf3] rounded-xl bg-white mb-6 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 flex items-center gap-3 border-b border-[#e8ebf3]">
        <FontAwesomeIcon icon={faExchangeAlt} className="text-teal-700 w-4 h-4" />
        <span className="font-bold text-gray-900 text-lg">Compare Pricing</span>
      </div>

      {/* Table */}
      <div className="bg-[#f8fafe]/30">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-[#e8ebf3] bg-[#f8fafe]/50">
              <th className="py-3 px-6 text-[10px] font-bold text-gray-500 uppercase w-1/3">
                Pricing Metrics
              </th>
              {comparePkgs.map((pkg, idx) => {
                const starsMatch = pkg.name?.match(/^(\d)\s*Star/i);
                const stars = starsMatch ? parseInt(starsMatch[1], 10) : (idx === 0 ? 3 : 4);
                return (
                  <th key={idx} className={`py-3 px-6 text-[10px] font-bold uppercase w-1/3 ${idx === 0 ? 'text-teal-700' : 'text-blue-700'}`}>
                    {stars} Star Package
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-[#e8ebf3]">
              <td className="py-4 px-6 text-sm font-medium text-gray-600">Per Person (Double Sharing)</td>
              <td className="py-4 px-6 text-sm font-semibold text-gray-900">{getPrice(comparePkgs[0], 'perPerson')}</td>
              <td className="py-4 px-6 text-sm font-semibold text-gray-900">{getPrice(comparePkgs[1], 'perPerson')}</td>
            </tr>
            <tr className="border-b border-[#e8ebf3]">
              <td className="py-4 px-6 text-sm font-medium text-gray-600">Per Adult Extra Bed</td>
              <td className="py-4 px-6 text-sm text-gray-600">{getPrice(comparePkgs[0], 'aeb')}</td>
              <td className="py-4 px-6 text-sm text-gray-600">{getPrice(comparePkgs[1], 'aeb')}</td>
            </tr>
            <tr className="border-b border-[#e8ebf3]">
              <td className="py-4 px-6 text-sm font-medium text-gray-600">Per Child With Bed</td>
              <td className="py-4 px-6 text-sm text-gray-600">{getPrice(comparePkgs[0], 'cwb')}</td>
              <td className="py-4 px-6 text-sm text-gray-600">{getPrice(comparePkgs[1], 'cwb')}</td>
            </tr>
            <tr className="border-b border-[#e8ebf3]">
              <td className="py-4 px-6 text-sm font-medium text-gray-600">Land Package Total</td>
              <td className="py-4 px-6 text-sm text-gray-600">{getPrice(comparePkgs[0], 'landTotal')}</td>
              <td className="py-4 px-6 text-sm text-gray-600">{getPrice(comparePkgs[1], 'landTotal')}</td>
            </tr>
            {includeTransport && (
              <tr className="border-b border-[#e8ebf3]">
                <td className="py-4 px-6 text-sm font-medium text-gray-600">Transport Total</td>
                <td className="py-4 px-6 text-sm text-gray-600">{getPrice(comparePkgs[0], 'transport')}</td>
                <td className="py-4 px-6 text-sm text-gray-600">{getPrice(comparePkgs[1], 'transport')}</td>
              </tr>
            )}
            <tr>
              <td className="py-4 px-6 text-sm font-bold text-gray-900 uppercase">Grand Total</td>
              <td className="py-4 px-6 text-base font-bold text-teal-700">{getPrice(comparePkgs[0], 'grandTotal')}</td>
              <td className="py-4 px-6 text-base font-bold text-blue-700">{getPrice(comparePkgs[1], 'grandTotal')}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DesktopCompareDetails;
